/**
 * Config for any Model property availability.
 *
 * Represents whether a property/field is:
 * - `notAvailable` (level 0): not visible/available - highest priority, cannot be overridden
 * - `readOnly` (level 1): visible but cannot be edited - can only be overridden to notAvailable
 * - `editable` (level 2): visible and can be edited - lowest priority, can be overridden to any level
 *
 * This is used across the application to configure field visibility
 * and editability based on settings.
 *
 * @example
 * ```typescript
 * const clientAvailability: PropertyAvailability = 'editable';
 * const agreementAvailability: PropertyAvailability = 'readOnly';
 * ```
 */
export type PropertyAvailability = 'readOnly' | 'editable' | 'notAvailable';

/**
 * Hierarchical levels for PropertyAvailability.
 * Lower numeric values have higher priority and cannot be overridden to higher levels.
 *
 * @example
 * ```typescript
 * // notAvailable (level 0) cannot be changed to readOnly (level 1) or editable (level 2)
 * // readOnly (level 1) can be changed to notAvailable (level 0) but not editable (level 2)
 * // editable (level 2) can be changed to any level
 * ```
 */
export const PROPERTY_AVAILABILITY_LEVELS = {
  notAvailable: 0,
  readOnly: 1,
  editable: 2,
} as const;

/**
 * Validates if an availability override is allowed based on hierarchy rules.
 * A field can only be overridden to a level with equal or lower numeric value (higher priority).
 *
 * @param baseAvailability - The original availability level from configuration
 * @param overrideAvailability - The desired override availability level
 * @returns true if the override is allowed, false otherwise
 *
 * @example
 * ```typescript
 * canOverrideAvailability('notAvailable', 'readOnly'); // false - cannot increase level
 * canOverrideAvailability('readOnly', 'notAvailable'); // true - can decrease level
 * canOverrideAvailability('editable', 'readOnly'); // true - can decrease level
 * canOverrideAvailability('editable', 'editable'); // true - same level allowed
 * ```
 */
export function canOverrideAvailability(
  baseAvailability: PropertyAvailability,
  overrideAvailability: PropertyAvailability,
): boolean {
  const baseLevel = PROPERTY_AVAILABILITY_LEVELS[baseAvailability];
  const overrideLevel = PROPERTY_AVAILABILITY_LEVELS[overrideAvailability];
  // Can only override to same level or lower numeric value (higher priority)
  return overrideLevel <= baseLevel;
}

/**
 * Applies an availability override if it's valid according to hierarchy rules.
 * If the override is not allowed, returns the base availability unchanged.
 *
 * @param baseAvailability - The original availability level from configuration
 * @param overrideAvailability - The desired override availability level
 * @returns The effective availability after applying override rules
 *
 * @example
 * ```typescript
 * applyAvailabilityOverride('editable', 'readOnly'); // 'readOnly'
 * applyAvailabilityOverride('notAvailable', 'editable'); // 'notAvailable' (override rejected)
 * applyAvailabilityOverride('readOnly', 'notAvailable'); // 'notAvailable'
 * ```
 */
export function applyAvailabilityOverride(
  baseAvailability: PropertyAvailability,
  overrideAvailability: PropertyAvailability,
): PropertyAvailability {
  return canOverrideAvailability(baseAvailability, overrideAvailability)
    ? overrideAvailability
    : baseAvailability;
}
